import { VykazReport } from '@/components/vyroba/VykazReport';
import type { VykazData } from '@/components/vyroba/types';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { downloadVykazPdf } from '@/lib/vykazPdf';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Styly sestavy — na obrazovce bílé A4 listy na šedém pozadí, při tisku
 * (a „Uložit jako PDF") čisté stránky bez lišty. Vkládají se až tady, ať
 * jejich `@page` nekoliduje s A6 výrobním listem.
 */
const REPORT_CSS = `
  #vykaz-report { font-family: Arial, Helvetica, sans-serif; color: #000; }
  #vykaz-report .page {
    background: #fff;
    width: 210mm;
    min-height: 297mm;
    box-sizing: border-box;
    padding: 12mm;
    margin: 0 auto 16px;
    box-shadow: 0 2px 12px rgba(0,0,0,.18);
  }
  #vykaz-report table { border-collapse: collapse; width: 100%; }
  #vykaz-report th, #vykaz-report td { border: 1px solid #999; padding: 3px 5px; font-size: 9px; }
  #vykaz-report th { background: #eee; text-align: center; }
  #vykaz-report .vykaz-note { font-size: 13px; line-height: 1.65; }
  #vykaz-report .vykaz-note p { margin: 0 0 3px; }
  #vykaz-report .vykaz-note ul { list-style: disc; padding-left: 18px; margin: 3px 0; }
  #vykaz-report .vykaz-note ol { list-style: decimal; padding-left: 18px; margin: 3px 0; }
  @media print {
    .vykaz-toolbar { display: none !important; }
    body { background: #fff; }
    #vykaz-report .page {
      width: auto; min-height: 0; padding: 0; margin: 0;
      box-shadow: none; break-after: page;
    }
    #vykaz-report .page:last-child { break-after: auto; }
    @page { size: A4 portrait; margin: 12mm; }
  }
`;

const MONTHS = [
  'leden',
  'únor',
  'březen',
  'duben',
  'květen',
  'červen',
  'červenec',
  'srpen',
  'září',
  'říjen',
  'listopad',
  'prosinec',
];

/**
 * Výkaz práce ve vlastním panelu — otevírá se z tlačítka „PDF výkaz".
 * Stránka je bez menu, ať sestava vypadá jako hotový dokument; tlačítko
 * nahoře pošle stránku do tisku, kde se dá uložit jako PDF.
 */
export function VykazTiskPage() {
  const [params] = useSearchParams();
  const year = Number.parseInt(params.get('year') ?? '', 10);
  const month = Number.parseInt(params.get('month') ?? '', 10);
  const svadlenka = params.get('svadlenka') ?? '';

  const [data, setData] = useState<VykazData | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const el = document.createElement('style');
    el.textContent = REPORT_CSS;
    document.head.appendChild(el);
    return () => {
      el.remove();
    };
  }, []);

  useEffect(() => {
    document.title = `Výkaz práce ${String(month).padStart(2, '0')}/${year} — ${svadlenka}`;
    apiFetch<VykazData>(
      `/api/vyroba/vykazy?year=${year}&month=${month}&svadlenka=${encodeURIComponent(svadlenka)}`,
    )
      .then(setData)
      .catch((e: unknown) =>
        setErr(e instanceof ApiError ? e.describe() : 'Výkaz se nepodařilo načíst'),
      );
  }, [year, month, svadlenka]);

  // PDF se skládá až na kliknutí — knihovna se natáhne dynamicky.
  async function savePdf() {
    if (!data) return;
    setSaving(true);
    try {
      await downloadVykazPdf({
        data,
        note: data.note,
        errors: data.errors,
        svadlenka,
        month,
        year,
      });
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'PDF se nepodařilo vytvořit');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#6b7280] py-6">
      <div className="vykaz-toolbar mx-auto mb-4 flex max-w-[210mm] items-center justify-between gap-4 px-2">
        <div className="text-white">
          <div className="text-lg font-bold">
            Výkaz práce — {svadlenka}, {MONTHS[month - 1] ?? ''} {year}
          </div>
          <div className="text-sm opacity-80">Stáhne se hotové PDF, není potřeba nic tisknout.</div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <button
            type="button"
            onClick={() => window.print()}
            className="text-white/90 hover:text-white text-sm font-semibold underline cursor-pointer"
          >
            Tisk
          </button>
          <button
            type="button"
            onClick={savePdf}
            disabled={!data || saving}
            className="bg-action hover:bg-action-dark disabled:opacity-60 text-white text-[17px] font-bold rounded-lg px-8 py-3.5 cursor-pointer transition-colors"
          >
            {saving ? 'Připravuji…' : '💾 Uložit PDF'}
          </button>
        </div>
      </div>

      {err && (
        <p className="mx-auto max-w-[210mm] rounded-lg bg-white px-4 py-3 text-sm text-danger">
          {err}
        </p>
      )}

      {data && (
        <VykazReport
          data={data}
          note={data.note}
          errors={data.errors}
          svadlenka={svadlenka}
          month={month}
          year={year}
        />
      )}
    </div>
  );
}
